import { buildOidcRedirectUri } from './callback-path';
import type { IAuthProviderConfig } from './types';

/**
 * Scopes requested for every federated login, regardless of provider.
 *
 * `offline_access` is what yields a refresh token, without which
 * `getAccessTokenSilently` has nothing to renew from. `email` is requested but
 * cannot be relied on: an Entra tenant with no Exchange emits no `email` claim
 * at all, and no amount of app registration fixes that.
 */
const BASE_SCOPES = ['openid', 'profile', 'email', 'offline_access'] as const;

/**
 * A provider entry that has been through {@link validateOidcConfig}.
 *
 * `createOidcSettings` takes this rather than a raw entry, so an incomplete one
 * cannot reach the SDK by a route the compiler has not seen.
 */
export type TValidatedOidcProvider = IAuthProviderConfig & {
  issuer: string;
  clientId: string;
};

/**
 * The `UserManagerSettings` subset the adapter produces.
 *
 * Declared locally rather than imported from `oidc-client-ts` so this module
 * stays out of the chunk that ships the SDK.
 */
export interface IOidcSettings {
  authority: string;
  client_id: string;
  redirect_uri: string;
  silent_redirect_uri: '';
  scope: string;
  response_type: 'code';
  automaticSilentRenew: boolean;
  loadUserInfo: boolean;
}

const splitScopes = (scope: string | undefined): string[] =>
  scope?.split(/\s+/).filter(Boolean) ?? [];

/**
 * Whether the operator asked for a scope beyond the OIDC-reserved set.
 *
 * Entra keys the access token's audience off the resource scope, not off any
 * `audience` parameter: request only `openid`/`profile`/`email` and it issues a
 * token audienced to Microsoft Graph, which our API then rejects as if the app
 * registration were wrong.
 */
const hasResourceScope = (provider: IAuthProviderConfig): boolean =>
  splitScopes(provider.scope).some(
    scope => !BASE_SCOPES.includes(scope as (typeof BASE_SCOPES)[number]),
  );

/**
 * Builds the OIDC client settings for a configured provider.
 *
 * `userStore` is deliberately left at the SDK default (session storage): how a
 * federated session survives a reload is still an open question, and picking a
 * store here would pre-empt it.
 *
 * @param {TValidatedOidcProvider} provider - A validated `authProviders` entry
 * @returns {IOidcSettings} Settings for the OIDC user manager
 */
export const createOidcSettings = (
  provider: TValidatedOidcProvider,
): IOidcSettings => ({
  authority: provider.issuer,
  client_id: provider.clientId,
  redirect_uri: buildOidcRedirectUri(provider.key),
  // Left unset, the SDK falls back to `redirect_uri` and the iframe would boot
  // the whole app on our callback route. Empty so `signinSilent` fails fast.
  silent_redirect_uri: '',
  scope: [...new Set([...BASE_SCOPES, ...splitScopes(provider.scope)])].join(
    ' ',
  ),
  response_type: 'code',
  // No background renewal timer. `SilentRenewService` calls `signinSilent` on
  // the manager directly, which would bypass the adapter's in-flight lock and
  // redeem a rotating refresh token twice. Renewal is driven on demand by
  // `getAccessTokenSilently`, which is also how the Auth0 adapter behaves.
  automaticSilentRenew: false,
  // Entra's userinfo endpoint is Microsoft Graph, which will not accept the
  // api:// token we asked for. Claims come from the id_token instead.
  loadUserInfo: false,
});

/**
 * Reports whether a provider entry can start a federated login.
 *
 * Never throws — it is diagnostic only, and warns about the misconfiguration
 * that presents as a working login with a token our API rejects.
 *
 * @param {IAuthProviderConfig} provider - Entry from the runtime `authProviders` list
 * @returns {boolean} true when issuer and client id are both present
 */
export const validateOidcConfig = (
  provider: IAuthProviderConfig,
): provider is TValidatedOidcProvider => {
  if (!provider.issuer || !provider.clientId) {
    console.warn(
      `Incomplete OIDC provider configuration for "${provider.key}":`,
      { hasIssuer: !!provider.issuer, hasClientId: !!provider.clientId },
    );
    return false;
  }

  if (!hasResourceScope(provider)) {
    console.warn(
      `OIDC provider "${provider.key}" requests no resource scope. The access ` +
        `token will be audienced to the provider's own API (Microsoft Graph, ` +
        `for Entra) rather than to ours. Set "scopes" on the AUTH_PROVIDERS ` +
        `entry, e.g. "api://<app-id>/access_as_user".`,
    );
  }

  return true;
};
